import { graphqlClient } from '@/lib/graphql-client';
import { buildGetSkillsQuery } from '../graphql/queries';
import { INSERT_SKILL_MUTATION, UPDATE_SKILL_MUTATION } from '../graphql/mutations';
import {
  GetSkillsParams,
  SkillInsertInput,
  SkillsPagingResult,
  SkillUpdateInput,
} from '../types/skill.types';

/** Skill names compare without case and surrounding / repeated spaces ("React " = "react"). */
export const normalizeSkillName = (name: string) => name.trim().replace(/\s+/g, ' ').toLowerCase();

export const getSkills = async (
  params: GetSkillsParams
): Promise<{ Skills: SkillsPagingResult }> => {
  const { pageNo, pageSize, filter = {}, order } = params;
  return graphqlClient.query({
    query: buildGetSkillsQuery(filter, pageNo, pageSize, order),
  }) as Promise<{ Skills: SkillsPagingResult }>;
};

/**
 * Whether another skill of the same sub-category already has this name. The gateway has no
 * case-insensitive or compound unique check, so every skill of the sub-category is compared here.
 */
export const isSkillNameTaken = async (
  name: string,
  subCategoryId: string,
  exceptItemId?: string
): Promise<boolean> => {
  const wanted = normalizeSkillName(name);
  const pageSize = 500;
  for (let pageNo = 1; ; pageNo++) {
    const { Skills } = await getSkills({
      pageNo,
      pageSize,
      filter: { SubCategoryId: subCategoryId },
    });
    if (
      Skills.items.some(
        (skill) => skill.ItemId !== exceptItemId && normalizeSkillName(skill.Name) === wanted
      )
    )
      return true;
    if (!Skills.hasNextPage) return false;
  }
};

export const createSkill = async (input: SkillInsertInput) => {
  return graphqlClient.mutate({ query: INSERT_SKILL_MUTATION, variables: { input } });
};

export const updateSkill = async (params: { itemId: string; input: SkillUpdateInput }) => {
  const filter = JSON.stringify({ _id: params.itemId });
  return graphqlClient.mutate({
    query: UPDATE_SKILL_MUTATION,
    variables: { filter, input: params.input },
  });
};
